<template>
    <th :class="{'table-search-open': show}" class="table-search">
        <span v-if="sortField" :id="labelId" v-sort:[defaultSort]="sortField" class="table-search-text" role="columnheader"><slot v-bind="{show}" name="text">{{text}}</slot></span>
        <span v-else :id="labelId" class="table-search-text" role="columnheader" tabindex="0"><slot name="text">{{text}}</slot></span>

        <div v-if="show" ref="searchWrapper" class="table-search-input-wrapper">
            <div class="table-search-input-container">
                <div ref="focusTrapEntry" aria-hidden="true" tabindex="0" @focus="blur(false, focusTrapEntry, true)"></div>
                <input v-if="false === options && !datepicker" ref="inputField" v-model="inputValue" v-debounce="update" v-focus :aria-labelledby="labelId" type="text"/>
                <form-input-date v-else-if="datepicker" v-model="inputValue" :aria-labelledby="labelId" :focus="true" hide-clear @change="update"/>
                <form-select v-else ref="select" v-model="inputValue" :aria-labelledby="labelId" :focus="selectFocus" :inherit-width="false" :multiple="!singleOption"
                    :options="options" class="table-search-select" @input="update" @mounted="focusOpenSelect" :with-expand-icon="false"/>
                <i aria-hidden="true" aria-label="Schließen" class="material-icons md-12 pointer table-search-close" tabindex="0" @keydown.space.prevent
                    @click.stop="blur(true)" @keyup.enter.stop="blur(true)"
                    @keyup.space.stop="blur(true)" role="img">close</i>
                <div ref="focusTrapExit" aria-hidden="true" tabindex="0" @focus="blur(false, focusTrapExit, true)"></div>
            </div>

            <slot/>
        </div>
        <template v-else>
            <span v-interact="open" class="icon-interact" aria-label="Spalte durchsuchen" ref="searchElement">
                <i class="material-icons md-18 table-search-icon" aria-hidden="true" role="img">search</i>
            </span>
            <slot/>
        </template>
    </th>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import {focusNextElement, focusPreviousElement} from '@/library/focus';
    import {uniqueId} from '@/library/string';
    import FormInputDate from './form/FormInputDate.vue';
    import FormSelect from './form/FormSelect.vue';
    import {ref, onBeforeUnmount, watch} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        'update:modelValue': [_inputValue0: string];
    }>();

    const props = withDefaults(
        defineProps<{
            modelValue?: string;
            text?: string;
            sortField?: string | string[];
            defaultSort?: boolean | 'asc' | 'desc';
            options?: false | [] | {};
            singleOption?: boolean;
            datepicker?: boolean;
        }>(),
        {
            modelValue: '',
            text: '',
            sortField: '',
            defaultSort: false,
            options: false,
            singleOption: false,
            datepicker: false,
        },
    );

    const focusTrapEntry = ref<HTMLDivElement>();
    const focusTrapExit  = ref<HTMLDivElement>();
    const searchElement  = ref<HTMLElement>();
    const select         = ref<FormSelect>();
    const searchWrapper  = ref<HTMLDivElement>();

    const show           = ref<boolean>(false);
    const inputValue     = ref<string>('');
    const selectFocus    = ref<boolean>(false);
    const lastKey        = ref('');
    const labelId        = uniqueId('label');
    const autoOpenSelect = ref<boolean>(false);

    const setLastKey = (event: KeyboardEvent | PointerEvent) =>
    {
        lastKey.value = '';

        if (!(event instanceof PointerEvent))
        {
            lastKey.value = (event.shiftKey ? 'Shift' : '') + event.code;
        }
    };

    const setValue = (newValue: string) =>
    {
        inputValue.value = newValue;

        emit('update:modelValue', inputValue.value);
    };

    const update = (newValue: string) =>
    {
        setValue(newValue);
    };

    const open = (event: Nullable<KeyboardEvent | MouseEvent> = null) =>
    {
        show.value        = true;
        selectFocus.value = true;

        // to prevent select from opening if value is changed without an event
        autoOpenSelect.value = Boolean(event && props.options && !props.datepicker);
    };

    const focusOpenSelect = () =>
    {
        if (!autoOpenSelect.value)
        {
            return;
        }

        select.value!.open();
    };

    const close = () =>
    {
        show.value = false;

        setValue('');
    };

    const blur = (force: boolean, context: Nullable<HTMLElement> = null, focusNextOnTab = false) =>
    {
        window.setTimeout(() =>
        {
            const isClose = force || !props.modelValue;

            isClose && close();

            if (context && 'Tab' === lastKey.value)
            {
                focusNextOnTab && focusNextElement(context);

                return;
            }

            if (context && 'ShiftTab' === lastKey.value)
            {
                focusNextOnTab && focusPreviousElement(context);

                return;
            }

            if (isClose)
            {
                window.setTimeout(() =>
                {
                    searchElement.value?.focus();
                }, 50);
            }
            else
            {
                focusNextElement(focusTrapExit.value!);
            }
        }, 0);
    };

    const toggle = () =>
    {
        show.value ? close() : open();
    };

    show.value       = '' !== props.modelValue;
    inputValue.value = props.modelValue;

    window.addEventListener('keydown', setLastKey);

    onBeforeUnmount(() =>
    {
        window.removeEventListener('keydown', setLastKey);
    });

    watch(() => props.modelValue, (newValue: string) =>
    {
        inputValue.value = newValue;
        '' !== newValue && open();
    });

    defineExpose(Object.assign(
        {},
        portuna,
        {
            componentName: 'TableSearch',
            toggle,
            blur,
            close,
            focusOpenSelect,
            open,
            update,
            setValue,
            setLastKey,
            show,
            inputValue,
            selectFocus,
            lastKey,
            labelId,
            autoOpenSelect,
        },
    ));
</script>